import { commitSchema } from './commit'
import { generate } from './generate'
import { baseSchema, Update } from './base'
import { z } from 'zod'
import { inviteSchema } from './invite-base'

const tokenScopeEnum = z.enum(['read', 'write'])

/* -------------------------------------------------------------------------- */
/*                                 Integration                                */
/* -------------------------------------------------------------------------- */

export const integrationSchema = z.object({
  id: z.string(),
  type: z.enum(['vercel']),
  token: z.object({
    token: z.string(),
    type: z.enum(['Bearer']),
  }),
  configurationId: z.string().nullish(),
  teamId: z.string().nullish(), // teamId is the vercel teamId,
  tokenScope: tokenScopeEnum.nullish(),
})

export type Integration = z.infer<typeof integrationSchema>
export type IntegrationUpdate = Update<Integration>

export const integrationBaseOps = generate(
  'integration',
  integrationSchema.parse
)
export const getIntegrationKey = (id: string) => `integration/${id}`

/* -------------------------------------------------------------------------- */
/*                                    Repo                                    */
/* -------------------------------------------------------------------------- */

export const tokenSchema = z.object({
  token: z.string(),
  scope: tokenScopeEnum,
})
export type RepoToken = z.infer<typeof tokenSchema>

export const repoForkStatusSchema = z.object({
  fromRepoId: z.string(),
  lastSyncedAt: z.string(),
  fromBranchId: z.string().nullish(),
  fromCommitId: z.string().nullish(),
})
export type RepoForkStatus = z.infer<typeof repoForkStatusSchema>

export const vercelDeployButtonSchema = z.object({
  isEnabled: z.boolean(),
  gitRepoUrl: z.string(),
  demoUrl: z.string().nullish(),
  envVars: z.string().nullish(),
  tokenScope: tokenScopeEnum.nullish(),
})
export type VercelDeployButtonConfig = z.infer<typeof vercelDeployButtonSchema>

export const readmeScreenshotsSchema = z.array(
  z.object({
    id: z.string(),
    alt: z.string(),
    url: z.string(),
    width: z.number(),
    height: z.number(),
    mimeType: z.string(),
  })
)

export type READMEScreenshots = z.infer<typeof readmeScreenshotsSchema>

export const readmeAdditionalInfoSchema = z.object({
  overview: z.string().optional(),
  useCase: z.array(z.string()),
  framework: z.array(z.string()),
  techStack: z.array(z.string()),
})

export type READMEAdditionalInfo = z.infer<typeof readmeAdditionalInfoSchema>

export const guestSchema = z.object({
  userId: z.string(),
  createdAt: z.string(),
})

export type Guest = z.infer<typeof guestSchema>

export const repositorySchema = baseSchema.extend({
  migrated: z.boolean().nullish(),
  title: z.string(),
  description: z.string().nullish(),
  slug: z.string(),
  createdAt: z.string(),
  lastTransferredAt: z.string().nullish(),
  forkStatus: repoForkStatusSchema.nullish(),
  isPublic: z.boolean().nullish().default(false),
  stats: z
    .object({
      bookmarks: z.number(),
      forks: z.number(),
    })
    .nullish(),
  latestCommit: commitSchema.nullable(),
  contributors: z.array(z.string()),
  tokens: tokenSchema.array(),
  status: z
    .enum(['active', 'transferring', 'deleted'])
    .optional()
    .nullable()
    .default('active'),
  previewUrl: z.string().nullish(),
  gitRepoUrl: z.string().nullish(),
  assistantId: z.string().nullish(),
  readme: z
    .object({
      vercelDeployButton: vercelDeployButtonSchema.nullish(),
      screenshots: readmeScreenshotsSchema.nullish(),
      additionalInfo: readmeAdditionalInfoSchema.nullish(),
    })
    .nullish(),
  integrations: z
    .array(
      z.object({
        id: integrationSchema.shape.id,
        type: integrationSchema.shape.type,
        projectId: z.string().optional(), // projectId is the vercel projectId
      })
    )
    .nullish(),
  branching: z
    .object({
      archiveAfterMerging: z.boolean(),
    })
    .optional(),
  guests: z
    .object({
      invites: inviteSchema.array(),
      list: guestSchema.array(),
    })
    .nullish(),
})

export type Repository = z.infer<typeof repositorySchema>
export type RepositoryUpdate = Update<Repository>

export const repositoryBaseOps = generate('repository', repositorySchema.parse)
export const getRepositoryKey = (id: string) => `repository/${id}`
